/* ==========================================================================
   gencon-crosssell.js
   &Collar — General Conference / Sunday cross-sell section

   Builds variant pickers from live Shopify data emitted by the section and
   adds to cart over the AJAX Cart API, so cross-sells accumulate instead of
   replacing the cart (which /cart/{id}:{qty} permalinks do).

   A module has one or more "groups" (each group is a real Shopify product).
   Group-level dimensions (e.g. Sleeve + Style, or Fit) choose the product;
   the product's own option names choose the variant. Rows with only one
   possible value are hidden.
   ========================================================================== */
(function () {
  'use strict';

  function boot(root) {
    var dataEl = root.querySelector('script[data-gc-data]');
    if (!dataEl) return;

    var DATA;
    try { DATA = JSON.parse(dataEl.textContent); }
    catch (e) { console.warn('[gencon] bad section data', e); return; }

    var MONEY = DATA.moneyFormat || '${{amount}}';
    var FREE_SHIP = DATA.freeShipCents || 0;
    var SHIP_UNDER = DATA.freeShipUnder || "You're [amount] from free shipping";
    var SHIP_MET = DATA.freeShipMet || 'Free shipping unlocked';
    // A module auto-selects a valid variant on load so the page reads as a working
    // store. That is NOT the shopper choosing — only entries flagged `touched`
    // count toward the fit, the totals and the sticky bar.
    var picked = Object.create(null);

    function formatMoney(cents) {
      var value = (Math.round(cents) / 100).toFixed(2).split('.');
      var whole = value[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      var amount = whole + '.' + value[1];
      return MONEY
        .replace(/\{\{\s*amount\s*\}\}/g, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole);
    }

    /* ---------------- toast ---------------- */
    var toastEl = root.querySelector('[data-gc-toast]');
    var toastTimer = null;
    function toast(msg) {
      if (!toastEl) return;
      toastEl.textContent = msg;
      toastEl.hidden = false;
      toastEl.classList.add('is-up');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () {
        toastEl.classList.remove('is-up');
        setTimeout(function () { toastEl.hidden = true; }, 260);
      }, 2600);
    }

    /* ---------------- cart ---------------- */
    function notifyTheme() {
      ['cart:refresh', 'cart:updated', 'cart:build'].forEach(function (name) {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
      });
      if (window.Shopify && typeof window.Shopify.onCartUpdate === 'function') {
        try { window.Shopify.onCartUpdate(); } catch (e) {}
      }
    }

    function addToCart(items) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) {
          return r.json().then(function (j) { throw new Error(j.description || 'Add to cart failed'); });
        }
        return r.json();
      }).then(function (res) { notifyTheme(); return res; });
    }

    /* ---------------- fit builder ---------------- */
    var slipList = root.querySelector('[data-gc-slip]');
    var slipTotal = root.querySelector('[data-gc-total]');
    var slipBtn = root.querySelector('[data-gc-addall]');
    var slipCart = root.querySelector('[data-gc-cartlink]');

    var barEl = root.querySelector('[data-gc-bar]');
    var barCount = root.querySelector('[data-gc-bar-count]');
    var barShip = root.querySelector('[data-gc-bar-ship]');
    var barTotal = root.querySelector('[data-gc-bar-total]');
    var barAdd = root.querySelector('[data-gc-bar-add]');
    var shipWrap = root.querySelector('[data-gc-ship]');
    var shipFill = root.querySelector('[data-gc-ship-fill]');
    var shipText = root.querySelector('[data-gc-ship-text]');

    function chosen() {
      return Object.keys(picked).filter(function (k) { return picked[k] && picked[k].touched; });
    }
    function pending() {
      return chosen()
        .filter(function (k) { return !picked[k].added; })
        .map(function (k) { return { id: picked[k].id, quantity: 1 }; });
    }
    function chosenTotal() {
      return chosen().reduce(function (sum, k) { return sum + picked[k].price; }, 0);
    }
    function shipCopy(total) {
      if (!FREE_SHIP) return null;
      if (total >= FREE_SHIP) return { met: true, text: SHIP_MET, pct: 100 };
      return {
        met: false,
        text: SHIP_UNDER.replace(/\[amount\]/g, formatMoney(FREE_SHIP - total)),
        pct: Math.max(4, Math.round((total / FREE_SHIP) * 100))
      };
    }

    function renderSlip() {
      var keys = chosen();
      var total = chosenTotal();
      var todo = pending();
      var ship = shipCopy(total);

      if (slipList) {
        var html = '';
        keys.forEach(function (k) {
          var p = picked[k];
          html += '<li' + (p.added ? ' class="is-added"' : '') + '><span>' + p.label +
                  (p.added ? ' <em>in cart</em>' : '') + '</span><span>' + formatMoney(p.price) + '</span></li>';
        });
        if (!keys.length) html = '<li class="is-empty"><span>Nothing picked yet</span><span>&mdash;</span></li>';
        slipList.innerHTML = html;
      }
      if (slipTotal) slipTotal.textContent = formatMoney(total);

      if (shipWrap) {
        if (ship && keys.length) {
          shipWrap.hidden = false;
          shipWrap.classList.toggle('is-met', ship.met);
          if (shipFill) shipFill.style.width = ship.pct + '%';
          if (shipText) shipText.textContent = ship.text;
        } else {
          shipWrap.hidden = true;
        }
      }

      if (slipBtn) {
        slipBtn.disabled = todo.length === 0;
        slipBtn.textContent = todo.length === 0
          ? (keys.length ? 'Everything is in your cart' : 'Pick something above')
          : (todo.length === 1 ? 'Add 1 item to cart' : 'Add all ' + todo.length + ' to cart');
      }
      if (slipCart) slipCart.hidden = !keys.some(function (k) { return picked[k].added; });

      if (barEl) {
        if (!keys.length) {
          barEl.hidden = true;
        } else {
          barEl.hidden = false;
          if (barCount) barCount.textContent = keys.length === 1 ? '1 item picked' : keys.length + ' items picked';
          if (barTotal) barTotal.textContent = formatMoney(total);
          if (barShip) {
            barShip.textContent = ship ? ship.text : '';
            barShip.classList.toggle('is-met', !!(ship && ship.met));
          }
          if (barAdd) {
            barAdd.disabled = todo.length === 0;
            barAdd.textContent = todo.length === 0 ? 'In your cart'
              : (todo.length === 1 ? 'Add to cart' : 'Add all ' + todo.length);
          }
        }
      }
    }

    function addEverything(btn) {
      var items = pending();
      if (!items.length) return;
      var was = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      addToCart(items).then(function () {
        chosen().forEach(function (k) { picked[k].added = true; });
        toast(items.length === 1 ? 'Added to your cart' : items.length + ' items added to your cart');
        renderSlip();
      }).catch(function (err) {
        btn.disabled = false;
        btn.textContent = was;
        toast('Could not add that — please try again');
        console.warn('[gencon]', err);
      });
    }
    if (slipBtn) slipBtn.addEventListener('click', function () { addEverything(slipBtn); });
    if (barAdd) barAdd.addEventListener('click', function () { addEverything(barAdd); });

    /* ---------------- ties (markup from Liquid) ---------------- */
    root.querySelectorAll('[data-gc-add]').forEach(function (btn) {
      btn.setAttribute('data-gc-idle', btn.textContent);
      btn.addEventListener('click', function () {
        var role = btn.getAttribute('data-gc-role') || 'tie';
        var id = parseInt(btn.getAttribute('data-gc-add'), 10);
        var price = parseInt(btn.getAttribute('data-gc-price'), 10) || 0;
        var label = btn.getAttribute('data-gc-label') || 'Item';
        btn.textContent = 'Adding…';
        addToCart([{ id: id, quantity: 1 }]).then(function () {
          root.querySelectorAll('[data-gc-role="' + role + '"]').forEach(function (other) {
            if (other !== btn) {
              other.textContent = other.getAttribute('data-gc-idle') || 'Add';
              other.classList.remove('is-picked');
            }
          });
          btn.textContent = 'In cart ✓';
          btn.classList.add('is-picked');
          picked[role] = { id: id, price: price, label: label, added: true, touched: true };
          toast('Tie added to your cart');
          renderSlip();
        }).catch(function (err) {
          btn.textContent = 'Try again';
          console.warn('[gencon]', err);
        });
      });
    });

    /* ---------------- lazy UGC clips ----------------
       Mirrors the perf fix on lds-landing: preload="none" + poster, the real
       source only attaches when the clip scrolls near the viewport. */
    (function lazyClips() {
      var vids = [].slice.call(root.querySelectorAll('video.gc-lazyvid'));
      if (!vids.length) return;

      function load(v) {
        if (v.dataset.gcLoaded) return;
        var src = v.getAttribute('data-src');
        if (!src) return;
        v.dataset.gcLoaded = '1';
        var source = document.createElement('source');
        source.src = src;
        source.type = 'video/mp4';
        v.appendChild(source);
        v.load();
        var play = v.play();
        if (play && play.catch) play.catch(function () {});
      }

      if (!('IntersectionObserver' in window)) { vids.forEach(load); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          load(entry.target);
          io.unobserve(entry.target);
        });
      }, { rootMargin: '300px 0px' });
      vids.forEach(function (v) { io.observe(v); });
    })();

    /* ---------------- variant picker ---------------- */
    function buildModule(mod) {
      var host = root.querySelector('.gc-pdp[data-gc-module="' + mod.key + '"]');
      if (!host || !mod.groups || !mod.groups.length) return;

      var gdims = mod.groupDims || (mod.groupDim ? [mod.groupDim] : []);
      var multi = mod.groups.length > 1 && gdims.length > 0;
      var nG = multi ? gdims.length : 0;
      var groupIdx = 0;
      // true once the shopper actually picks something in this module; the
      // auto-selected default on load does not count.
      var touched = false;
      // selection is positional, not keyed by name: a product option can share a
      // name with a group dimension (Range Pant has its own "Fit" option).
      var selv = [];

      function group() { return mod.groups[groupIdx]; }
      function labelsOf(g) { return g.labels || [g.label]; }
      function dims() { return (multi ? gdims : []).concat(group().options || []); }
      function rows() {
        var pre = multi ? labelsOf(group()) : [];
        return (group().variants || []).map(function (v) {
          return { id: v.id, o: pre.concat(v.o), a: v.a, p: v.p };
        });
      }
      function live() { return rows().filter(function (r) { return r.a; }); }
      function groupHasStock(g) { return (g.variants || []).some(function (v) { return v.a; }); }

      host.innerHTML =
        '<div class="gc-pdp-media"><img alt="" data-gc-img></div>' +
        '<div class="gc-pdp-body">' +
          (mod.eyebrow ? '<p class="gc-eyebrow gc-eyebrow--ink" data-gc-eyebrow></p>' : '') +
          '<h3 data-gc-heading></h3>' +
          (mod.kicker ? '<p class="gc-kicker" data-gc-kicker></p>' : '') +
          '<p class="gc-price" data-gc-price></p>' +
          '<div class="gc-opts"></div>' +
          '<p class="gc-stock" role="status" data-gc-stock></p>' +
          '<div class="gc-cta-row">' +
            '<button type="button" class="gc-btn gc-btn--navy" data-gc-addbtn>Add to cart</button>' +
            '<a class="gc-btn gc-btn--outline" data-gc-view href="#">See full details</a>' +
          '</div>' +
          (mod.note ? '<p class="gc-pdp-note" data-gc-note></p>' : '') +
        '</div>';

      var imgEl = host.querySelector('[data-gc-img]');
      var optsWrap = host.querySelector('.gc-opts');
      var stockEl = host.querySelector('[data-gc-stock]');
      var priceEl = host.querySelector('[data-gc-price]');
      var addBtn = host.querySelector('[data-gc-addbtn]');
      var viewEl = host.querySelector('[data-gc-view]');
      if (mod.eyebrow) host.querySelector('[data-gc-eyebrow]').textContent = mod.eyebrow;
      host.querySelector('[data-gc-heading]').textContent = mod.heading || '';
      if (mod.kicker) host.querySelector('[data-gc-kicker]').textContent = mod.kicker;
      if (mod.note) host.querySelector('[data-gc-note]').textContent = mod.note;

      var startIdx = mod.groups.findIndex(groupHasStock);
      groupIdx = startIdx > -1 ? startIdx : 0;
      var seed = live()[0] || rows()[0];
      if (!seed) {
        var wrapper = host.closest('section');
        if (wrapper) { wrapper.remove(); } else { host.remove(); }
        return;
      }
      selv = seed.o.slice();

      // Open on a mid-range size, not the first in the list. The smallest size is a
      // bad default for menswear and it arms the Add button with an XS, so a shopper
      // who skims and clicks can buy the wrong thing.
      (function seedMidSize() {
        var ds = dims();
        for (var di = 0; di < ds.length; di++) {
          if (!/size/i.test(ds[di])) continue;
          var vals = values(di).filter(function (v) { return available(di, v); });
          if (vals.length < 3) break;
          selv[di] = vals[Math.floor((vals.length - 1) / 2)];
          repair();
          break;
        }
      })();

      function currentVariant() {
        return live().filter(function (r) {
          return r.o.every(function (val, i) { return val === selv[i]; });
        })[0] || null;
      }

      function values(di) {
        var out = [];
        if (multi && di < nG) {
          mod.groups.forEach(function (g) {
            var v = labelsOf(g)[di];
            if (v != null && out.indexOf(v) === -1) out.push(v);
          });
        } else {
          rows().forEach(function (r) {
            if (out.indexOf(r.o[di]) === -1) out.push(r.o[di]);
          });
        }
        return out;
      }

      function available(di, val) {
        if (multi && di < nG) {
          return mod.groups.some(function (g) {
            var ls = labelsOf(g);
            if (ls[di] !== val) return false;
            for (var j = 0; j < nG; j++) { if (j !== di && ls[j] !== selv[j]) return false; }
            return groupHasStock(g);
          });
        }
        return live().some(function (r) {
          if (r.o[di] !== val) return false;
          return r.o.every(function (v, j) { return j === di || v === selv[j]; });
        });
      }

      function chooseGroup(di, val) {
        var prevOpts = {};
        (group().options || []).forEach(function (name, j) { prevOpts[name] = selv[nG + j]; });

        var want = labelsOf(group()).slice();
        want[di] = val;
        var idx = mod.groups.findIndex(function (g) {
          var ls = labelsOf(g);
          return want.every(function (w, i) { return ls[i] === w; }) && groupHasStock(g);
        });
        if (idx < 0) {
          idx = mod.groups.findIndex(function (g) {
            return labelsOf(g)[di] === val && groupHasStock(g);
          });
        }
        if (idx < 0) return;
        groupIdx = idx;

        // carry as much of the shopper's previous choice across as the new
        // product can honour: score every in-stock variant, take the best.
        var names = group().options || [];
        var pool = live().length ? live() : rows();
        var best = null, bestScore = -1;
        pool.forEach(function (r) {
          var score = 0;
          names.forEach(function (name, j) {
            if (prevOpts[name] != null && r.o[nG + j] === prevOpts[name]) score++;
          });
          if (score > bestScore) { bestScore = score; best = r; }
        });
        selv = (best || pool[0]).o.slice();
      }

      function repair() {
        if (currentVariant()) return;
        for (var keep = selv.length - 1; keep >= 0; keep--) {
          var match = live().filter(function (r) {
            for (var i = 0; i < keep; i++) { if (r.o[i] !== selv[i]) return false; }
            return true;
          })[0];
          if (match) { selv = match.o.slice(); return; }
        }
        var any = live()[0];
        if (any) selv = any.o.slice();
      }

      function render() {
        var ds = dims();
        optsWrap.innerHTML = '';

        ds.forEach(function (dim, di) {
          var vals = values(di);
          if (vals.length < 2) return;

          var block = document.createElement('div');
          block.className = 'gc-opt';
          block.innerHTML = '<div class="gc-opt-label"><span></span><b></b></div><div class="gc-opt-row"></div>';
          block.querySelector('span').textContent = dim;
          block.querySelector('b').textContent = selv[di] || '';
          var row = block.querySelector('.gc-opt-row');

          vals.forEach(function (val) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'gc-chip';
            b.textContent = val;
            var isOn = selv[di] === val;
            b.setAttribute('aria-pressed', isOn ? 'true' : 'false');
            b.disabled = !available(di, val) && !isOn;
            b.addEventListener('click', function () {
              touched = true;
              if (multi && di < nG) { chooseGroup(di, val); }
              else { selv[di] = val; repair(); }
              render();
            });
            row.appendChild(b);
          });
          optsWrap.appendChild(block);
        });

        var g = group();
        if (imgEl) {
          if (g.image) { imgEl.src = g.image; imgEl.alt = g.alt || ''; imgEl.hidden = false; }
          else { imgEl.hidden = true; }
        }
        if (viewEl) viewEl.setAttribute('href', g.url || '#');

        var v = currentVariant();
        if (v) {
          var shownParts = [];
          dims().forEach(function (d, i) { if (values(i).length > 1) shownParts.push(selv[i]); });
          var label = (mod.eyebrow || mod.heading || 'Item') +
                      (shownParts.length ? ' · ' + shownParts.join(' / ') : '');
          var prev = picked[mod.role];
          priceEl.textContent = formatMoney(v.p);
          addBtn.disabled = false;
          addBtn.textContent = 'Add to cart — ' + formatMoney(v.p);
          stockEl.textContent = 'In stock and ready to ship.';
          stockEl.className = 'gc-stock';
          picked[mod.role] = {
            id: v.id, price: v.p, label: label,
            touched: touched,
            added: !!(prev && prev.added && prev.id === v.id)
          };
        } else {
          priceEl.textContent = formatMoney(g.price || 0);
          addBtn.disabled = true;
          addBtn.textContent = 'Sold out';
          stockEl.textContent = 'That combination is out of stock.';
          stockEl.className = 'gc-stock is-low';
          delete picked[mod.role];
        }
        renderSlip();
      }

      addBtn.addEventListener('click', function () {
        var v = currentVariant();
        if (!v) return;
        touched = true;
        addBtn.disabled = true;
        addBtn.textContent = 'Adding…';
        addToCart([{ id: v.id, quantity: 1 }]).then(function () {
          addBtn.textContent = 'In cart ✓';
          if (picked[mod.role]) { picked[mod.role].added = true; picked[mod.role].touched = true; }
          toast('Added to your cart');
          renderSlip();
          setTimeout(function () { render(); }, 2200);
        }).catch(function (err) {
          addBtn.disabled = false;
          addBtn.textContent = 'Could not add — try again';
          toast('Could not add that — please try again');
          console.warn('[gencon]', err);
        });
      });

      render();
    }

    (DATA.modules || []).forEach(buildModule);
    renderSlip();
  }

  function init() {
    document.querySelectorAll('.gc-lp').forEach(function (root) {
      if (root.dataset.gcReady) return;
      root.dataset.gcReady = '1';
      boot(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('.gc-lp');
    if (root) { root.dataset.gcReady = '1'; boot(root); }
  });
})();
